import {
  BoxPlotFilled,
  CameraOutlined,
  CodeOutlined,
  CompassOutlined,
  CopyrightOutlined,
  DatabaseOutlined,
  MessageOutlined,
  ProfileOutlined,
} from "@ant-design/icons";
import { Image, Spin } from "antd";
import React, { Suspense } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthProvider/AuthProvider";
import { Config } from "../Config/Config";
import { Contact } from "../Contact/Contact";
import { Customer } from "../Customer/Customer";
import { CustomerRoute } from "../CustomerRoute/CustomerRoute";
import { DocDisplay } from "../DocDisplay/DocDisplay";
import { Donate } from "../Donate/Donate";
import { PaymentComplete } from "../Donate/PaymentComplete";
import { FileUpload } from "../FileUpload/FileUpload";
import { FileUploads } from "../FileUpload/FileUploads";
import { Transcribe } from "../FileUpload/Transcribe";
import { FinanceLibrary } from "../Finances/FinanceLibrary";
import { FinanceReports } from "../Finances/FinanceReports";
import DataDeletionPage from "../Instruction/Instruction";
import { Invoices } from "../Invoices/Invoices";
import { Library } from "../Library/Library";
import { Login } from "../Login/Login";
import { MemberRoute } from "../MemberRoute/MemberRoute";
import MetaManager from "../MetaManager/MetaManager";
import { NoAccess } from "../NoAccess/NoAccess";
import PrivacyPolicyPage from "../Policy/Privacy";
import { PrivateRoute } from "../PrivateRoute/PrivateRoute";
import { Projects } from "../Projects/PublicProjects";
import { CreateEvent } from "../Tasks/CreateEvent";
import { Tasks } from "../Tasks/Tasks";
import { Teleprompter } from "../Teleprompter/Teleprompter";
import TermsOfServicePage from "../Terms/Terms";
import logo from "./../Images/SWSNoBG.png";
import image4 from "./300 WSM_1.21.1.jpg";
import image1 from "./Still 2026-02-15 004440_2.22.7.png";
import image2 from "./_THA2765-01.jpeg";
import image3 from "./dji.jpg";
import image5 from "./bg_image.jpg";
import ResourceUploadPage from "../Resources/ResourceUploadPage";
import ResourceGalleryPage from "../Resources/ResourceGallery";

const Tools = React.lazy(() => import("../Tools/Tools"));

const WebsiteManagement = React.lazy(
  () => import("../WebsiteManagement/WebsiteManagement"),
);

export const Foreground = () => {
  const nav = useNavigate();
  const loc = useLocation();
  const { currentUser, logout } = useAuth();

  const ServiceItem = ({ icon, title, desc }: any) => (
    <div className="group p-12 border-r border-[#9A6D26]/20 last:border-r-0 hover:bg-white/[0.02] transition-colors">
      <div className="text-[#F6B34B] mb-8 group-hover:scale-110 transition-transform duration-500">
        {icon}
      </div>
      <h3 className="font-oswald text-2xl uppercase mb-4 tracking-tight group-hover:text-[#F6B34B] transition-colors">
        {title}
      </h3>
      <p className="text-[#5C6B73] font-mono text-sm leading-relaxed uppercase">
        {desc}
      </p>
    </div>
  );

  const GalleryImage = ({ span, src, label }: any) => (
    <div
      className={`relative group overflow-hidden border border-white/10 ${span}`}
    >
      <div className="absolute inset-0 bg-[#1D1928] transition-transform duration-700">
        <img
          src={src}
          alt={label}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700 scale-110 group-hover:scale-100"
        />
      </div>
      {/* UI Tags */}
      <div className="absolute top-4 left-4 font-mono text-[9px] text-[#F6B34B] opacity-0 group-hover:opacity-100 transition-opacity">
        [ {label} ]
      </div>
      <div className="absolute bottom-0 left-0 w-full p-4 bg-gradient-to-t from-black/80 to-transparent translate-y-full group-hover:translate-y-0 transition-transform">
        <span className="font-mono text-[10px] uppercase tracking-widest text-white/60">
          Data_Capture_Active
        </span>
      </div>
    </div>
  );

  if (loc.pathname === "/")
    return (
      <div className="flex flex-col items-center bg-[#1f1e23] text-white selection:bg-[#F6B34B] selection:text-[#1D1928]">
        {/* 1. HERO SECTION: "Wilderness To Webflow" */}
        <section className="relative h-screen w-full overflow-hidden font-mono items-center content-center flex flex-col">
          <img
            src={image5}
            alt="Scryptworx Studios field production background"
            className="absolute top-0 left-0 h-full w-full object-cover opacity-60"
            decoding="async"
            fetchPriority="high"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-[#1f1e23] via-[#313035]/80 to-transparent" />

          <div className="relative z-10 flex flex-col justify-center h-full max-w-7xl mx-auto px-6">
            <div className="text-[#5C6B73] text-xs mb-4 tracking-[0.3em] font-mono animate-pulse">
              // SYSTEM.READY :: RENDER_MODE_ACTIVE
            </div>
            <div className="flex flex-col items-center w-full mb-20">
              <Image
                src={logo}
                alt="SCRYPTWORX STUDIOS"
                className="mb-10 mt-10 w-full max-w-lg"
                preview={false}
              />
            </div>
            {/* <h1 className="text-6xl md:text-5xl font-bold uppercase tracking-tighter text-[#F6B34B] mb-6 font-oswald leading-[0.85]">
                Wilderness <br />
                <span className="text-white">To Webflow.</span>
              </h1> */}

            <p className="text-gray-300 max-w-xl text-lg mb-10 border-l-2 border-[#F6B34B] pl-4 font-mono leading-relaxed">
              We specialize in high-stakes film production and full-stack custom
              development.
              <span className="block mt-2 text-[#5C6B73]">
                We build the content, then we build the platform it lives on.
              </span>
            </p>

            <div className="flex flex-row gap-4 w-full items-center justify-center">
              {/* <button className="bg-[#F6B34B] text-[#1D1928] font-bold px-8 py-4 hover:bg-[#FAD48A] transition-all uppercase tracking-widest text-sm font-oswald">
                  [ View Showreel ]
                </button> */}
              <button
                className="border border-[#5C6B73] text-[#5C6B73] px-8 py-4 hover:border-[#F6B34B] hover:text-[#F6B34B] transition-all uppercase tracking-widest text-sm font-mono"
                onClick={() => nav(currentUser ? "/tools" : "/login")}
              >
                {currentUser ? "Tools" : "Login"}
              </button>
              {currentUser && (
                <button
                  className="border border-[#5C6B73] text-[#5C6B73] px-8 py-4 hover:border-[#F6B34B] hover:text-[#F6B34B] transition-all uppercase tracking-widest text-sm font-mono"
                  onClick={() => logout()}
                >
                  Sign out
                </button>
              )}
            </div>
          </div>

          <div className="absolute bottom-0 w-full border-t border-white/10 py-4 bg-[#1D1928]/50 backdrop-blur-sm">
            <div className="flex justify-between max-w-7xl mx-auto px-6 text-[10px] font-mono text-gray-500 tracking-[0.2em]">
              <span>SCROLL_TO_EXPLORE</span>
              <span>EST. 2022 // NZ // 43°22'48.3"S</span>
            </div>
          </div>
        </section>

        {/* 2. THE PIPELINE: Vertical Services */}
        <section className="py-24 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#9A6D26]/20">
            <ServiceItem
              icon={<CameraOutlined size={32} />}
              title="Extreme Production"
              desc="Gear that survives the elements. Cinematography for wild and hostile environments."
            />
            <ServiceItem
              icon={<CodeOutlined size={32} />}
              title="Full-Stack Development"
              desc="Custom Vite, Next, React, Node, and Firebase architecture. We don't just build websites."
            />
            <ServiceItem
              icon={<DatabaseOutlined size={32} />}
              title="Analytics & Client Contact"
              desc="We don't just track clicks. We build to reach out to your clients and customers with purpose."
            />
          </div>
        </section>

        {/* 3. FIELD OPERATIONS: The Grid Gallery */}
        <section className="bg-black/20 py-24 border-y border-white/5 w-full">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-12">
              <h2 className="font-oswald text-4xl text-white uppercase tracking-tighter">
                Operations
              </h2>
              <div className="h-1 w-12 bg-[#F6B34B] mt-2" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-[250px]">
              <GalleryImage
                span="md:col-span-2 md:row-span-2"
                src={image1}
                label="Alpine"
              />
              <GalleryImage
                span="md:col-span-2 md:row-span-1"
                src={image2}
                label="Tropics"
              />
              <GalleryImage
                span="md:col-span-1 md:row-span-1"
                src={image3}
                label="Air"
              />
              <GalleryImage
                span="md:col-span-1 md:row-span-1"
                src={image4}
                label="Hunting"
              />
            </div>
          </div>
        </section>
        {/* 4. THE PIPELINE: Vertical Services */}
        <section className="py-24 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#9A6D26]/20">
            <ServiceItem
              icon={<ProfileOutlined size={32} />}
              title="Consultancy"
              desc="Experience in military operations, scientific research, and technical investigations."
            />
            <ServiceItem
              icon={<BoxPlotFilled size={32} />}
              title="3D Design, Print and Effects"
              desc="3D product and prop design. We can print and build it for you."
            />
            <ServiceItem
              icon={<CompassOutlined size={32} />}
              title="AI Integration"
              desc="Artificial intelligence integration for efficient systems."
            />
          </div>
        </section>
        <section className="py-24 max-w-7xl mx-auto px-6">
          <div className="pb-6">
            Interested in working together, get in touch.
          </div>
          <div className="grid grid-cols-1  gap-0 border border-[#9A6D26]/20">
            <button
              className="border border-[#5C6B73] px-5 py-3 font-mono text-xs uppercase tracking-widest text-[#5C6B73] transition-all hover:border-[#F6B34B] hover:text-[#F6B34B]"
              onClick={() => nav("/contact")}
              type="button"
            >
              Contact
            </button>
          </div>
        </section>
        <section className="bg-black/20  border-y border-white/5 w-full">
          <div className=" w-full border-t border-white/10 py-4 bg-[#1D1928]/50 backdrop-blur-sm">
            <div className="flex flex-col  max-w-7xl mx-auto px-6 text-[10px] font-mono text-gray-500 tracking-[0.2em]">
              <div className="flex gap-2 w-full text-xs  mx-auto">
                <a type="link" href="/privacy.html">
                  PRIVACY
                </a>
                -
                <a type="link" href="/instructions-on-removing-account">
                  REMOVE ACCOUNT
                </a>
                -
                <a type="link" href="/terms-and-conditions">
                  T & C
                </a>
              </div>
              <div className="flex w-full items-center gap-1 text-gray-500 mx-auto mt-2">
                <span>
                  <CopyrightOutlined />
                  {` SCRYPTWORX STUDIOS LIMITED ${new Date().getFullYear()}`}
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>
    );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignContent: "center",
        alignItems: "center",
      }}
    >
      <Routes>
        <Route
          path="/configuration"
          element={
            <Suspense fallback={<Spin size="large" />}>
              <Config />
            </Suspense>
          }
        />
        <Route
          path="/resource-upload-scryptworxstudios"
          element={
            <Suspense fallback={<Spin size="large" />}>
              <ResourceUploadPage />
            </Suspense>
          }
        />

        <Route
          path="/jtre89t043tjkndrfkjgnkiuu8e98ou984utjiojrtklnetker"
          element={
            <Suspense fallback={<Spin size="large" />}>
              <FileUpload />
            </Suspense>
          }
        />
        <Route path="/projects" element={<Projects />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/terms-and-conditions" element={<TermsOfServicePage />} />
        <Route
          path="/instructions-on-removing-account"
          element={<DataDeletionPage />}
        />

        <Route path="/unauthorised-access" element={<NoAccess />} />
        <Route
          path="/tools"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <Tools />
              </Suspense>
            </PrivateRoute>
          }
        />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route
          path="/transcribe"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <Transcribe />
              </Suspense>
            </PrivateRoute>
          }
        />
        <Route
          path="/resources"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <ResourceGalleryPage />
              </Suspense>
            </PrivateRoute>
          }
        />
        <Route
          path="/tasks"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <Tasks />
              </Suspense>
            </PrivateRoute>
          }
        />

        <Route
          path="/analysis-tools"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <FileUploads />
              </Suspense>
            </PrivateRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <CreateEvent />
              </Suspense>
            </PrivateRoute>
          }
        />
        {/* <Route
          path="/audio-analyser"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <AudioAnalyser />
              </Suspense>
            </PrivateRoute>
          }
        /> */}

        <Route
          path="/finance-library"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <FinanceLibrary />
              </Suspense>
            </PrivateRoute>
          }
        />
        <Route
          path="/finance-reports"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <FinanceReports />
              </Suspense>
            </PrivateRoute>
          }
        />
        <Route
          path="/meta-manager"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <MetaManager />
              </Suspense>
            </PrivateRoute>
          }
        />
        {/* <Route
          path="/clapperboard"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <ClapperBoard />
              </Suspense>
            </PrivateRoute>
          }
        /> */}

        <Route
          path="/website-management"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <WebsiteManagement />
              </Suspense>
            </PrivateRoute>
          }
        />
        <Route
          path="/donate"
          element={
            <MemberRoute>
              <Suspense fallback={<Spin size="large" />}>
                <Donate />
              </Suspense>
            </MemberRoute>
          }
        />
        {/* <Route
          path="/camera-control"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <CameraControl />
              </Suspense>
            </PrivateRoute>
          }
        /> */}
        <Route
          path="/invoices"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <Invoices />
              </Suspense>
            </PrivateRoute>
          }
        />
        <Route
          path="/library"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <Library />
              </Suspense>
            </PrivateRoute>
          }
        />
        <Route
          path="/library/doc"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <DocDisplay />
              </Suspense>
            </PrivateRoute>
          }
        />

        <Route
          path="/teleprompter"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <Teleprompter />
              </Suspense>
            </PrivateRoute>
          }
        />
        <Route
          path="/thanks"
          element={
            <PrivateRoute>
              <Suspense fallback={<Spin size="large" />}>
                <PaymentComplete />
              </Suspense>
            </PrivateRoute>
          }
        />
        <Route
          path="/order"
          element={
            <CustomerRoute>
              <Suspense fallback={<Spin size="large" />}>
                <Customer />
              </Suspense>
            </CustomerRoute>
          }
        />
      </Routes>
    </div>
  );
};
